import React, { useState, useEffect, useRef, useCallback } from "react";
import InputMask from "react-input-mask";
import {
  TextField,
  Autocomplete,
  Button,
  Typography,
  Grid,
  Container,
  Paper,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  FormControl,
  InputLabel,
  InputAdornment,
  Select,
  RadioGroup,
  Radio,
  IconButton,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";
import Navbar from "../../../../globals/components/Navbar";
import CreatableSelect from "react-select/creatable";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

import { apiClient, uploadGIA, uploadLOA } from "../../../../globals/utils/api";
import { loadGoogleMaps } from "../../../../globals/utils/googleMapsLoader";
import { normalizeUsState, isAllowedUsState } from "../../../../globals/utils/usStates";

// ------------------ GoogleAutoComplete ------------------
const GoogleAutoComplete = ({ placeholder, callback, label }) => {
  const autoCompleteRef = useRef();
  const inputRef = useRef();
  const [loaded, setLoaded] = useState(false);

  const loadGoogleMapsScript = useCallback(() => {
    loadGoogleMaps()
      .then(() => setLoaded(true))
      .catch((e) => console.error(e));
  }, []);

  useEffect(() => {
    loadGoogleMapsScript();
  }, [loadGoogleMapsScript]);

  useEffect(() => {
    if (loaded && window.google) {
      autoCompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        { fields: ["address_components"], types: ["address"] }
      );
      autoCompleteRef.current.addListener("place_changed", () => {
        const place = autoCompleteRef.current.getPlace();
        let address = "",
          city = "",
          state = "",
          county = "",
          zipCode = "";
        place.address_components.forEach((component) => {
          const type = component.types[0];
          switch (type) {
            case "street_number":
              address = `${component.long_name} ${address}`;
              break;
            case "route":
              address += component.long_name;
              break;
            case "locality":
              city = component.long_name;
              break;
            case "administrative_area_level_2":
              county = component.long_name;
              break;
            case "administrative_area_level_1":
              state = component.long_name;
              break;
            case "postal_code":
              zipCode = component.long_name;
              break;
            default:
              break;
          }
        });
        callback(address, city, state, county, zipCode);
        if (inputRef.current) {
          inputRef.current.value = "";
        }
      });
    }
  }, [loaded, callback]);

  return (
    <TextField
      label={label || "Search Address"}
      variant="outlined"
      fullWidth
      placeholder={placeholder}
      inputRef={inputRef}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton size="small" aria-label="search">
              <SearchIcon />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

GoogleAutoComplete.propTypes = {
  callback: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  label: PropTypes.string,
};

// ------------------ CustomCreatableSelect ------------------
const CustomCreatableSelect = ({
  label,
  options,
  value,
  onChange,
  placeholder,
  required,
  error = false,
}) => {
  return (
    <FormControl
      fullWidth
      variant="outlined"
      required={required}
      error={error}
      sx={{ marginBottom: "16px" }}
    >
      <InputLabel shrink>{label}</InputLabel>
      <Box
        sx={{
          border: error ? "1px solid #d32f2f" : "1px solid #ced4da",
          borderRadius: "4px",
          "&:hover": { borderColor: error ? "#d32f2f" : "#1976d2" },
          "&.Mui-focused": { borderColor: error ? "#d32f2f" : "#1976d2" },
        }}
      >
        <CreatableSelect
          isClearable
          options={options}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          styles={{
            control: (provided, state) => ({
              ...provided,
              border: "none",
              boxShadow: "none",
              background: "transparent",
              minHeight: "56px",
            }),
            valueContainer: (provided) => ({
              ...provided,
              padding: "6px 14px",
              fontSize: "16px",
            }),
            placeholder: (provided) => ({
              ...provided,
              color: "#9e9e9e",
            }),
            menu: (provided) => ({
              ...provided,
              zIndex: 1300, // Set a higher z-index value
            }),
            menuPortal: (base) => ({
              ...base,
              zIndex: 1300, // This is useful if the select menu is rendered in a portal
            }),
          }}
          menuPortalTarget={document.body} // Optional: render menu in a portal if necessary
        />
      </Box>
    </FormControl>
  );
};

// ------------------ Main Component ------------------
function CreateLawFirm() {
  const navigate = useNavigate();

  // STEPS
  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Law Firm Details", "Add Locations", "Add Users"];

  // LOADING & ID
  const [loading, setLoading] = useState(false);
  const [lawfirmid, setLawfirmid] = useState(null);

  // ------------------ STEP 0: LAW FIRM INFO ------------------
  const [lawFirmData, setLawFirmData] = useState({
    name: "",
    website: "",
    logo: null,
    accountingContactName: "",
    accountingContactEmail: "",
    accountingContactPhone: "",
    logoName: "",
    managed_by: null,
  });
  const [fuzzyMatchDialogOpen, setFuzzyMatchDialogOpen] = useState(false);
  const [fuzzyMatchResult, setFuzzyMatchResult] = useState(null);
  const [usedFuzzyMatchedLawFirm, setUsedFuzzyMatchedLawFirm] = useState(false);
  const [fuzzyMatchLocationDialogOpen, setFuzzyMatchLocationDialogOpen] = useState(false);
  const [fuzzyMatchLocationResult, setFuzzyMatchLocationResult] = useState(null);
  const [pendingLocationSubmitData, setPendingLocationSubmitData] = useState(null);

  const handleLawFirmChange = (e) => {
    const { name, value } = e.target;
    setLawFirmData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Optionally truncate the filename
      const truncatedFileName = file.name.slice(0, 50);
      setLawFirmData((prev) => ({
        ...prev,
        logo: file,
        logoName: truncatedFileName,
      }));
    }
  };

  const doCreateLawFirm = async () => {
    const formData = new FormData();
    if (lawFirmData.logo) formData.append("logo", lawFirmData.logo);
    formData.append("name", lawFirmData.name);
    formData.append("website", lawFirmData.website);
    formData.append(
      "accounting_contact_name",
      lawFirmData.accountingContactName
    );
    formData.append(
      "accounting_contact_email",
      lawFirmData.accountingContactEmail
    );
    formData.append(
      "accounting_contact_phone",
      lawFirmData.accountingContactPhone
    );
    if (lawFirmData.managed_by !== null) {
      formData.append("managed_by", lawFirmData.managed_by);
    }
    formData.append("is_new", true);
    formData.append("is_active", true);
    formData.append("created_by_guest", false);
    const response = await apiClient(
      "/create-a-lawfirm",
      "POST",
      formData,
      {},
      { "content-type": "multipart/form-data" }
    );
    setLawfirmid(response.lawfirm_id);
    setUsedFuzzyMatchedLawFirm(false);
    toast.success("Law Firm created successfully!");
    setActiveStep(1);
  };

  const handleCreateLawFirm = async () => {
    try {
      if (!lawFirmData.name) {
        toast.error("Please fill all the required fields");
        return;
      }
      setLoading(true);
      const fuzzyRes = await apiClient("/fuzzy-match-lawfirm", "POST", {
        name: lawFirmData.name,
      });
      const matchedId = fuzzyRes?.match?.lawfirm_id ?? fuzzyRes?.law_firm?.id ?? fuzzyRes?.lawfirm_id;
      const matchedName = fuzzyRes?.match?.name ?? fuzzyRes?.law_firm?.name ?? fuzzyRes?.law_firm_name ?? "";
      const hasMatch = (fuzzyRes?.match != null && matchedId != null) || (fuzzyRes?.matched && matchedId != null);
      if (hasMatch) {
        setLoading(false);
        setFuzzyMatchResult({ id: matchedId, name: matchedName || lawFirmData.name });
        setFuzzyMatchDialogOpen(true);
        return;
      }
      await doCreateLawFirm();
    } catch (error) {
      console.error("Error creating law firm:", error);
      toast.error("Failed to create Law Firm. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleUseExistingLawFirm = () => {
    if (!fuzzyMatchResult) return;
    setLawfirmid(fuzzyMatchResult.id);
    setUsedFuzzyMatchedLawFirm(true);
    setFuzzyMatchDialogOpen(false);
    setFuzzyMatchResult(null);
    toast.success("Using existing law firm.");
    setActiveStep(1);
  };

  const handleCreateNewLawFirmAnyway = async () => {
    setFuzzyMatchDialogOpen(false);
    setFuzzyMatchResult(null);
    try {
      setLoading(true);
      await doCreateLawFirm();
    } catch (error) {
      console.error("Error creating law firm:", error);
      toast.error("Failed to create Law Firm. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // --- Internal Users for Managed by ---
  const [internalUsers, setInternalUsers] = useState([]);

  const fetchInternalUsers = async () => {
    try {
      const internalUsers = await apiClient("/internal-user", "GET");
      setInternalUsers(internalUsers.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchInternalUsers();
  }, []);

  // ------------------ STEP 1: ADD LOCATIONS ------------------
  const [locations, setLocations] = useState([
    {
      name: "Main Location",
      address: "",
      zipCode: "",
      city: "",
      county: "",
      state: "",
      phone: "",
      fax: "",
      customerNumber: "",
      useSeparateAccounting: false,
      accountingContact: { name: "", email: "", phone: "" },
      addressTypes: { physical: false, mailing: false, billing: false },
    },
  ]);

  useEffect(() => {
    console.log(locations);
  }, [locations]);

  const [allCities, setAllCities] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [allCounties, setAllCounties] = useState([]);
  const [lawFirmLocations, setLawFirmLocations] = useState([]); // For Step 2 usage (fetched after creation)
  const [addressFieldErrors, setAddressFieldErrors] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const cities = await apiClient("/fields/city/", "GET");
        const states = await apiClient("/fields/states/", "GET");
        const counties = await apiClient("/fields/counties/", "GET");
        setAllCities(cities);
        setAllStates(states);
        setAllCounties(counties);
      } catch (error) {
        console.error("Error loading cities or states:", error);
      }
    };
    loadData();
  }, []);

  const handleLocationChange = (index, field, value) => {
    const updatedLocations = [...locations];
    updatedLocations[index][field] = value;
    setLocations(updatedLocations);
    setAddressFieldErrors((prev) => {
      const next = prev.slice();
      while (next.length <= index) next.push({});
      next[index] = { ...(next[index] || {}), [field]: false };
      return next;
    });
  };

  const addLocation = () => {
    setLocations((prev) => [
      ...prev,
      {
        name: `Location ${prev.length + 1}`,
        address: "",
        zipCode: "",
        city: "",
        country: "USA",
        county: "",
        state: "",
        phone: "",
        fax: "",
        customerNumber: "",
        useSeparateAccounting: false,
        accountingContact: { name: "", email: "", phone: "" },
        addressTypes: { physical: false, mailing: false, billing: false },
      },
    ]);
  };

  const removeLocation = (index) => {
    setLocations((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGoogleCallback = (
    index,
    address,
    city,
    state,
    county,
    zipCode
  ) => {
    handleLocationChange(index, "address", address);
    handleLocationChange(index, "city", city);
    handleLocationChange(index, "county", county);
    handleLocationChange(index, "state", normalizeUsState(state, allStates));
    handleLocationChange(index, "zipCode", zipCode);
  };

  const handleZipCodeChange = async (index, zipCode) => {
    handleLocationChange(index, "zipCode", zipCode);
    if (zipCode.length === 5) {
      try {
        const response = await apiClient(`/fields/location/${zipCode}`, "GET");
        const { city, state } = response;
        handleLocationChange(index, "city", city);
        handleLocationChange(index, "state", normalizeUsState(state, allStates));
      } catch (error) {
        console.error("Error fetching city/state by zip code:", error);
      }
    }
  };

  const handleCreateLocation = async () => {
    const errors = locations.map((loc) => ({
      address: !(loc.address && String(loc.address).trim()),
      zipCode: !(loc.zipCode && String(loc.zipCode).trim()),
      city: !(loc.city && String(loc.city).trim()),
      state: !(loc.state && String(loc.state).trim()),
      county: !(loc.county && String(loc.county).trim()),
    }));
    const hasErrors = errors.some((e) => e.address || e.zipCode || e.city || e.state || e.county);
    if (hasErrors) {
      setAddressFieldErrors(errors);
      toast.error("Please fill in all required address fields: Address, Zip Code, City, State, and County.");
      return;
    }
    setAddressFieldErrors([]);

    // Create location(s) for the newly created lawfirm
    const normalizedLocations = locations.map((location) => {
      const normalizedState = normalizeUsState(location.state, allStates);
      return { ...location, state: normalizedState };
    });

    const invalidState = normalizedLocations.find(
      (loc) => loc.state && !isAllowedUsState(loc.state, allStates)
    );
    if (invalidState) {
      toast.error(`Invalid state "${invalidState.state}". Please select a valid state.`);
      return;
    }

    const dataToSend = normalizedLocations.map((location) => ({
      lawfirm_id: lawfirmid,
      name: location.name,
      address: location.address,
      zip_code: location.zipCode,
      city: location.city,
      county: location.county,
      country: "USA",
      state: location.state,
      phone: location.phone,
      fax: location.fax,
      customer_number: location.customerNumber,
      use_separate_firm_accounting_contact: location.useSeparateAccounting,
      accounting_contact_full_name: location.accountingContact.name,
      accounting_contact_email: location.accountingContact.email,
      accounting_contact_phone: location.accountingContact.phone,
      is_physical_address: location.addressTypes.physical,
      is_mailing_address: location.addressTypes.mailing,
      is_billing_address: location.addressTypes.billing,
      is_active: true,
    }));

    // When user chose "Use existing" law firm (fuzzy match), check each location for duplicate
    if (usedFuzzyMatchedLawFirm) {
      for (let i = 0; i < dataToSend.length; i++) {
        const loc = dataToSend[i];
        try {
          const fuzzyRes = await apiClient("/fuzzy-match-lawfirm-location", "POST", {
            lawfirm_id: Number(lawfirmid),
            address: loc.address,
            city: loc.city,
            state: loc.state,
            zip_code: loc.zip_code,
            county: loc.county || "",
            country: "USA",
          });
          const matchedId = fuzzyRes?.match?.law_firm_location_id;
          if (fuzzyRes?.match != null && matchedId != null) {
            setPendingLocationSubmitData(dataToSend);
            setFuzzyMatchLocationResult({
              locationIndex: i,
              law_firm_location_id: matchedId,
              score: fuzzyRes?.score ?? 0,
              addressSummary: [loc.address, loc.city, loc.state, loc.zip_code].filter(Boolean).join(", "),
            });
            setFuzzyMatchLocationDialogOpen(true);
            return;
          }
        } catch (err) {
          console.error("Fuzzy match check failed for location:", err);
        }
      }
    }

    try {
      setLoading(true);
      await apiClient(`/lawfirm-location/`, "POST", dataToSend);

      // After creating locations, fetch them back so step 3 can reference them
      const newlyCreatedLocations = await apiClient(
        `/get-locations-by-lawfirm/${lawfirmid}`,
        "GET"
      );
      setLawFirmLocations(newlyCreatedLocations);
      toast.success("Locations created successfully!");
      setActiveStep(2); // Move to step 3 (Add Users)
    } catch (error) {
      console.error("Error creating locations:", error);
      toast.error("Failed to create locations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleUseExistingLocation = async () => {
    if (!pendingLocationSubmitData || fuzzyMatchLocationResult == null) return;
    const index = fuzzyMatchLocationResult.locationIndex;
    const filtered = pendingLocationSubmitData.filter((_, i) => i !== index);
    setFuzzyMatchLocationDialogOpen(false);
    setFuzzyMatchLocationResult(null);
    setPendingLocationSubmitData(null);
    try {
      setLoading(true);
      if (filtered.length > 0) {
        await apiClient(`/lawfirm-location/`, "POST", filtered);
      }
      const newlyCreatedLocations = await apiClient(
        `/get-locations-by-lawfirm/${lawfirmid}`,
        "GET"
      );
      setLawFirmLocations(newlyCreatedLocations);
      toast.success(filtered.length > 0 ? "Locations created successfully!" : "Skipped creating duplicate location.");
      setActiveStep(2);
    } catch (error) {
      console.error("Error creating locations:", error);
      toast.error("Failed to create locations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewLocationAnyway = async () => {
    if (!pendingLocationSubmitData) return;
    const dataToSend = pendingLocationSubmitData;
    setFuzzyMatchLocationDialogOpen(false);
    setFuzzyMatchLocationResult(null);
    setPendingLocationSubmitData(null);
    try {
      setLoading(true);
      await apiClient(`/lawfirm-location/`, "POST", dataToSend);
      const newlyCreatedLocations = await apiClient(
        `/get-locations-by-lawfirm/${lawfirmid}`,
        "GET"
      );
      setLawFirmLocations(newlyCreatedLocations);
      toast.success("Locations created successfully!");
      setActiveStep(2);
    } catch (error) {
      console.error("Error creating locations:", error);
      toast.error("Failed to create locations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ------------------ STEP 2: ADD USERS ------------------

  const positionOptions = ["Paralegal", "Attorney", "Clerical Staff"];

  const [lawSchools, setLawSchools] = useState([]);
  const [legalServiceProfessionals, setLegalServiceProfessionals] = useState(
    []
  );
  // We'll fetch these once we reach step 2
  useEffect(() => {
    const fetchStep2Data = async () => {
      if (activeStep === 2) {
        try {
          setLoading(true);
          const [lawSchoolsResponse, professionalsResponse] = await Promise.all(
            [
              apiClient("/lawschools/", "GET"),
              apiClient("/legal-service-paralegals/", "GET"),
            ]
          );
          setLawSchools(lawSchoolsResponse.data);
          setLegalServiceProfessionals(
            professionalsResponse.map((pro) => ({
              id: pro.lawfirm_user_id,
              name: `${pro.prefix} ${pro.preferred_name}`,
            }))
          );
        } catch (error) {
          console.error("Error loading data for user creation:", error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchStep2Data();
  }, [activeStep]);

  // Each user has separate location
  const [users, setUsers] = useState([
    {
      prefix: "",
      firstName: "",
      lastName: "",
      middleName: "",
      suffix: "",
      email: "",
      useFirmEmail: false,
      firmEmail: "",
      phoneNumber: "",
      extension: "",
      position: "",
      permissionLevel: "User",
      isLegalService: false,
      isDefault: false,
      lawschool: "",
      barNumber: "",
      selectedProfessional: "",
      showCustomForm: false,
      is_gia_on_file: false,
      gia_document_link: "",
      gia_document_name: "",
      gia_file: null,
      is_loa_on_file: false,
      loa_document_link: "",
      loa_document_name: "",
      loa_file: null,
      location: "",
      sendInvite: false,
    },
  ]);

  const handleUserInputChange = (index, field, value) => {
    const updatedUsers = [...users];
    updatedUsers[index] = { ...updatedUsers[index], [field]: value };

    // Reset dependent fields if the position changes
    if (field === "position") {
      updatedUsers[index].isLegalService = false;
      updatedUsers[index].selectedProfessional = "";
      updatedUsers[index].showCustomForm = false;
    }

    setUsers(updatedUsers);
  };

  const handleAddUser = () => {
    setUsers((prev) => [
      ...prev,
      {
        prefix: "",
        firstName: "",
        lastName: "",
        middleName: "",
        suffix: "",
        email: "",
        useFirmEmail: false,
        firmEmail: "",
        phoneNumber: "",
        extension: "",
        position: "",
        permissionLevel: "User",
        isLegalService: false,
        isDefault: false,
        lawschool: "",
        barNumber: "",
        selectedProfessional: "",
        is_gia_on_file: false,
        gia_document_link: "",
        gia_document_name: "",
        gia_file: null,
        showCustomForm: false,
        location: "",
        sendInvite: false,
      },
    ]);
  };

  const handleRemoveUser = (index) => {
    setUsers(users.filter((_, i) => i !== index));
  };

  // Build a preferred name for the user
  const buildPreferredName = (user) => {
    return [
      user.prefix,
      user.firstName,
      user.middleName,
      user.lastName,
      user.suffix,
    ]
      .filter((name) => name)
      .join(" ");
  };

  const handleSubmitAllUsers = async () => {
    setLoading(true);
    try {
      const submissions = users.map(async (user) => {
        // Handle GIA upload first if needed
        let gia_document_link = user.gia_document_link;
        let gia_document_name = user.gia_document_name;
        let loa_document_link = user.loa_document_link;
        let loa_document_name = user.loa_document_name;

        if (
          user.position === "Attorney" &&
          user.gia_file &&
          user.is_gia_on_file
        ) {
          const uploadResponse = await uploadGIA(user.gia_file);
          gia_document_link = uploadResponse.file_url;
          gia_document_name = uploadResponse.file_name;
        }

        if (
          user.position === "Attorney" &&
          user.loa_file &&
          user.is_loa_on_file
        ) {
          const uploadResponse = await uploadLOA(user.loa_file);
          loa_document_link = uploadResponse.file_url;
          loa_document_name = uploadResponse.file_name;
        }

        // If user is an existing paralegal from the list
        if (user.selectedProfessional) {
          return apiClient("/create-lawfirm-location-users", "POST", {
            lawfirm_id: lawfirmid,
            law_firm_location_id: user.location,
            lawfirm_user_id: user.selectedProfessional,
          });
        }

        // Otherwise, invite a brand new user
        return apiClient("/invite-lawfirm-user", "POST", {
          user: {
            email: user.email,
            first_name: user.firstName,
            last_name: user.lastName,
          },
          law_firm_location_id: user.location,
          lawfirm_id: lawfirmid,
          title: user.position,
          position: user.position,
          middle_name: user.middleName,
          phone_number: user.phoneNumber,
          suffix: user.suffix,
          prefix: user.prefix,
          preferred_name: buildPreferredName(user),
          extension: user.extension,
          bar: user.barNumber,
          is_default: user.isDefault,
          is_legal_service: user.isLegalService,
          is_gia_on_file: user.is_gia_on_file,
          gia_document_link: gia_document_link,
          gia_document_name: gia_document_name,
          is_loa_on_file: user.is_loa_on_file,
          loa_document_link,
          loa_document_name,
          is_active: true,
          is_admin: user.permissionLevel === "Admin",
          lawschool: user.lawschool,
          send_invite: user.sendInvite,
          firm_email: user.useFirmEmail ? user.firmEmail : null,
        });
      });

      await Promise.all(submissions);
      const anyInvited = users.some((u) => u.sendInvite);
      toast.success(
        anyInvited
          ? "Users created and invites sent successfully!"
          : "Users created successfully!"
      );
      setLoading(false);

      // After success, you may reset or navigate
      navigate(`/lawfirmdetails/${lawfirmid}`);
      // or setActiveStep(3) if there's a final summary step
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Error submitting form");
    } finally {
      setLoading(false);
    }
  };

  // ------------------ RENDER ------------------
  return (
    <>
      <Navbar />
      <Container maxWidth="md" sx={{ marginTop: 4 }}>
        <Paper
          sx={{ padding: 4, backgroundColor: "#f5f5f5", borderRadius: "15px" }}
        >
          <Typography
            variant="h4"
            gutterBottom
            style={{ textAlign: "center", fontWeight: "bold" }}
          >
            Create New Law Firm
          </Typography>

          <Stepper
            activeStep={activeStep}
            alternativeLabel
            style={{ marginBottom: "20px" }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {loading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50px",
                marginBottom: "16px",
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {/* ------------------ STEP 0: LAW FIRM DETAILS ------------------ */}
          {activeStep === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Law Firm Name"
                  name="name"
                  fullWidth
                  required
                  variant="outlined"
                  value={lawFirmData.name}
                  onChange={handleLawFirmChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  label="Managed By"
                  name="managed_by"
                  fullWidth
                  variant="outlined"
                  value={lawFirmData.managed_by || ""}
                  onChange={handleLawFirmChange}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {internalUsers.map((user) => (
                    <MenuItem
                      key={user.internal_user_id}
                      value={user.internal_user_id}
                    >
                      {user.full_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  component="label"
                  sx={{ textTransform: "none", marginBottom: 2 }}
                >
                  Upload Logo
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </Button>
                {lawFirmData.logoName && (
                  <Typography variant="body2" sx={{ marginTop: 1 }}>
                    {lawFirmData.logoName}...
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Website"
                  name="website"
                  fullWidth
                  variant="outlined"
                  value={lawFirmData.website}
                  onChange={handleLawFirmChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Accounting Contact Full Name"
                  name="accountingContactName"
                  fullWidth
                  variant="outlined"
                  value={lawFirmData.accountingContactName}
                  onChange={handleLawFirmChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Accounting Contact Email"
                  name="accountingContactEmail"
                  type="email"
                  fullWidth
                  variant="outlined"
                  value={lawFirmData.accountingContactEmail}
                  onChange={handleLawFirmChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputMask
                  mask="(999) 999-9999"
                  value={lawFirmData.accountingContactPhone}
                  onChange={handleLawFirmChange}
                >
                  {() => (
                    <TextField
                      label="Accounting Contact Phone"
                      name="accountingContactPhone"
                      fullWidth
                      variant="outlined"
                    />
                  )}
                </InputMask>
              </Grid>

              <Grid item xs={12} style={{ textAlign: "right" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateLawFirm}
                >
                  CREATE LAW FIRM
                </Button>
              </Grid>
            </Grid>
          )}

          <Dialog open={fuzzyMatchDialogOpen} onClose={() => { setFuzzyMatchDialogOpen(false); setFuzzyMatchResult(null); }}>
            <DialogTitle>Similar law firm found</DialogTitle>
            <DialogContent>
              <Typography>
                A similar law firm was found: <strong>{fuzzyMatchResult?.name}</strong>. Do you want to use this existing firm or create a new one?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleUseExistingLawFirm} color="primary">
                Use existing
              </Button>
              <Button onClick={handleCreateNewLawFirmAnyway} color="secondary">
                Create new anyway
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={fuzzyMatchLocationDialogOpen} onClose={() => { setFuzzyMatchLocationDialogOpen(false); setFuzzyMatchLocationResult(null); setPendingLocationSubmitData(null); }}>
            <DialogTitle>Similar location found</DialogTitle>
            <DialogContent>
              <Typography>
                A similar location already exists for this address{fuzzyMatchLocationResult?.addressSummary ? `: ${fuzzyMatchLocationResult.addressSummary}` : ""}{fuzzyMatchLocationResult?.score ? ` (match score: ${Number(fuzzyMatchLocationResult.score).toFixed(0)}%)` : ""}. Do you want to use the existing location or create this new one anyway?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleUseExistingLocation} color="primary">
                Use existing
              </Button>
              <Button onClick={handleCreateNewLocationAnyway} color="secondary">
                Create new anyway
              </Button>
            </DialogActions>
          </Dialog>

          {/* ------------------ STEP 1: ADD LOCATIONS ------------------ */}
          {activeStep === 1 && (
            <>
              {locations.map((location, index) => (
                <Box
                  key={index}
                  sx={{
                    padding: 2,
                    marginBottom: 3,
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Location Name"
                        name="name"
                        fullWidth
                        variant="outlined"
                        value={location.name}
                        onChange={(e) =>
                          handleLocationChange(index, "name", e.target.value)
                        }
                        sx={{ marginTop: 2, marginBottom: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <GoogleAutoComplete
                        label="Search Address"
                        placeholder="Search for an address (e.g., 600 Brickell Ave)"
                        callback={(address, city, state, county, zipCode) =>
                          handleGoogleCallback(
                            index,
                            address,
                            city,
                            state,
                            county,
                            zipCode
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        label="Address"
                        name="address"
                        fullWidth
                        variant="outlined"
                        value={location.address}
                        onChange={(e) =>
                          handleLocationChange(index, "address", e.target.value)
                        }
                        error={!!addressFieldErrors[index]?.address}
                        helperText={addressFieldErrors[index]?.address ? "Required" : ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Zip Code"
                        name="zipCode"
                        fullWidth
                        required
                        variant="outlined"
                        value={location.zipCode}
                        onChange={(e) =>
                          handleZipCodeChange(index, e.target.value)
                        }
                        error={!!addressFieldErrors[index]?.zipCode}
                        helperText={addressFieldErrors[index]?.zipCode ? "Required" : ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <CustomCreatableSelect
                        label="City"
                        options={allCities.map((city) => ({
                          label: city,
                          value: city,
                        }))}
                        value={
                          location.city
                            ? { label: location.city, value: location.city }
                            : null
                        }
                        onChange={(opt) =>
                          handleLocationChange(
                            index,
                            "city",
                            opt ? opt.value : ""
                          )
                        }
                        placeholder="Select a city"
                        error={!!addressFieldErrors[index]?.city}
                      />
                      {addressFieldErrors[index]?.city && (
                        <Typography variant="caption" color="error" sx={{ mt: -1, mb: 1, display: "block" }}>Required</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <CustomCreatableSelect
                        label="State"
                        options={allStates.map((st) => ({
                          label: st,
                          value: st,
                        }))}
                        value={
                          location.state
                            ? { label: location.state, value: location.state }
                            : null
                        }
                        onChange={(opt) => {
                          const next = normalizeUsState(opt ? opt.value : "", allStates);
                          handleLocationChange(index, "state", next);
                        }}
                        placeholder="Select a state"
                        required
                        error={!!addressFieldErrors[index]?.state}
                      />
                      {addressFieldErrors[index]?.state && (
                        <Typography variant="caption" color="error" sx={{ mt: -1, mb: 1, display: "block" }}>Required</Typography>
                      )}
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      sx={{ mt: 0 }}
                    >
                      <FormControl fullWidth error={!!addressFieldErrors[index]?.county}>
                        <CustomCreatableSelect
                          label="County"
                          options={allCounties.map((county) => ({
                            label: county,
                            value: county,
                          }))}
                          value={
                            location.county
                              ? {
                                label: location.county,
                                value: location.county,
                              }
                              : null
                          }
                          onChange={(opt) =>
                            handleLocationChange(
                              index,
                              "county",
                              opt ? opt.value : ""
                            )
                          }
                          placeholder="Select or type a county"
                          error={!!addressFieldErrors[index]?.county}
                        />
                        {addressFieldErrors[index]?.county && (
                          <Typography variant="caption" color="error" sx={{ mt: -1, mb: 1, display: "block" }}>Required</Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <InputMask
                        mask="(999) 999-9999"
                        value={location.phone}
                        onChange={(e) =>
                          handleLocationChange(index, "phone", e.target.value)
                        }
                      >
                        {(inputProps) => (
                          <TextField
                            {...inputProps}
                            label="Phone"
                            name="phone"
                            fullWidth
                            variant="outlined"
                          />
                        )}
                      </InputMask>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Fax"
                        name="fax"
                        fullWidth
                        variant="outlined"
                        value={location.fax}
                        onChange={(e) =>
                          handleLocationChange(index, "fax", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        required
                        label="Customer Number"
                        name="customerNumber"
                        fullWidth
                        variant="outlined"
                        value={location.customerNumber}
                        onChange={(e) =>
                          handleLocationChange(
                            index,
                            "customerNumber",
                            e.target.value
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={location.useSeparateAccounting}
                            onChange={() =>
                              handleLocationChange(
                                index,
                                "useSeparateAccounting",
                                !location.useSeparateAccounting
                              )
                            }
                          />
                        }
                        label="Use separate accounting contact for this location"
                      />
                    </Grid>
                    {location.useSeparateAccounting && (
                      <>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Accounting Contact Name"
                            name="name"
                            fullWidth
                            variant="outlined"
                            value={location.accountingContact.name}
                            onChange={(e) =>
                              handleLocationChange(index, "accountingContact", {
                                ...location.accountingContact,
                                name: e.target.value,
                              })
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Accounting Contact Email"
                            name="email"
                            fullWidth
                            variant="outlined"
                            value={location.accountingContact.email}
                            onChange={(e) =>
                              handleLocationChange(index, "accountingContact", {
                                ...location.accountingContact,
                                email: e.target.value,
                              })
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Accounting Contact Phone"
                            name="phone"
                            fullWidth
                            variant="outlined"
                            value={location.accountingContact.phone}
                            onChange={(e) =>
                              handleLocationChange(index, "accountingContact", {
                                ...location.accountingContact,
                                phone: e.target.value,
                              })
                            }
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={location.addressTypes.physical}
                            onChange={() =>
                              handleLocationChange(index, "addressTypes", {
                                ...location.addressTypes,
                                physical: !location.addressTypes.physical,
                              })
                            }
                          />
                        }
                        label="Physical Address"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={location.addressTypes.mailing}
                            onChange={() =>
                              handleLocationChange(index, "addressTypes", {
                                ...location.addressTypes,
                                mailing: !location.addressTypes.mailing,
                              })
                            }
                          />
                        }
                        label="Mailing Address"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={location.addressTypes.billing}
                            onChange={() =>
                              handleLocationChange(index, "addressTypes", {
                                ...location.addressTypes,
                                billing: !location.addressTypes.billing,
                              })
                            }
                          />
                        }
                        label="Billing Address"
                      />
                    </Grid>
                    {locations.length > 1 && (
                      <Grid item xs={12} sx={{ textAlign: "right" }}>
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => removeLocation(index)}
                          sx={{ marginTop: 2 }}
                        >
                          Remove Location
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              ))}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "16px",
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={addLocation}
                  startIcon={<AddIcon />}
                >
                  ADD ANOTHER LOCATION
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateLocation}
                  disabled={loading} // Disable button when loading
                >
                  {loading ? (
                    <CircularProgress
                      size={24}
                      sx={{
                        color: "white",
                      }}
                    />
                  ) : (
                    "Create Locations"
                  )}
                </Button>
              </Box>
            </>
          )}

          {/* ------------------ STEP 2: ADD USERS ------------------ */}
          {activeStep === 2 && (
            <>
              {users.map((user, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 4,
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="h6">User {index + 1}</Typography>
                        {users.length > 1 && (
                          <IconButton
                            onClick={() => handleRemoveUser(index)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </Grid>

                    {/* Location and Position */}
                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>Law Firm Location</InputLabel>
                        <Select
                          value={user.location}
                          onChange={(e) =>
                            handleUserInputChange(
                              index,
                              "location",
                              e.target.value
                            )
                          }
                          label="Law Firm Location"
                        >
                          <MenuItem value="" disabled>
                            Select location
                          </MenuItem>
                          {lawFirmLocations.map((location) => (
                            <MenuItem
                              key={location.law_firm_location_id}
                              value={location.law_firm_location_id}
                            >
                              {location.name} - {location.address}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={12}>
                      <FormControl fullWidth required>
                        <InputLabel>Position</InputLabel>
                        <Select
                          value={user.position}
                          onChange={(e) =>
                            handleUserInputChange(
                              index,
                              "position",
                              e.target.value
                            )
                          }
                          label="Position"
                        >
                          <MenuItem value="">Select Position</MenuItem>
                          {positionOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Attorney-specific fields */}
                    {user.position === "Attorney" && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            options={lawSchools}
                            getOptionLabel={(option) => option.name || ""}
                            value={
                              lawSchools.find(
                                (school) =>
                                  school.lawschool_id === user.lawschool
                              ) || null
                            }
                            onChange={(event, newValue) =>
                              handleUserInputChange(
                                index,
                                "lawschool",
                                newValue ? newValue.lawschool_id : ""
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Law School"
                                variant="outlined"
                                fullWidth
                                required
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Bar Number"
                            fullWidth
                            value={user.barNumber}
                            required
                            onChange={(e) =>
                              handleUserInputChange(
                                index,
                                "barNumber",
                                e.target.value
                              )
                            }
                          />
                        </Grid>
                      </>
                    )}

                    {/* Paralegal fields */}
                    {user.position === "Paralegal" && !user.showCustomForm && (
                      <Grid item xs={12}>
                        <FormControl component="fieldset">
                          <Typography variant="subtitle1">
                            Is this individual an Independent Paralegal who
                            works for multiple law firms?
                          </Typography>
                          <RadioGroup
                            row
                            value={user.isLegalService ? "yes" : "no"}
                            onChange={(e) =>
                              handleUserInputChange(
                                index,
                                "isLegalService",
                                e.target.value === "yes"
                              )
                            }
                          >
                            <FormControlLabel
                              value="yes"
                              control={<Radio />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="no"
                              control={<Radio />}
                              label="No"
                            />
                          </RadioGroup>
                        </FormControl>

                        {user.isLegalService && (
                          <>
                            <Select
                              fullWidth
                              value={user.selectedProfessional}
                              onChange={(e) =>
                                handleUserInputChange(
                                  index,
                                  "selectedProfessional",
                                  e.target.value
                                )
                              }
                              displayEmpty
                              sx={{ mt: 2 }}
                            >
                              <MenuItem value="" disabled>
                                Select Professional...
                              </MenuItem>
                              {legalServiceProfessionals.map((professional) => (
                                <MenuItem
                                  key={professional.id}
                                  value={professional.id}
                                >
                                  {professional.name}
                                </MenuItem>
                              ))}
                            </Select>
                            <Button
                              onClick={() =>
                                handleUserInputChange(
                                  index,
                                  "showCustomForm",
                                  true
                                )
                              }
                              sx={{ mt: 1 }}
                            >
                              Legal Service Professional not in list?
                            </Button>
                          </>
                        )}
                      </Grid>
                    )}

                    {/* General fields */}
                    {(user.showCustomForm ||
                      !user.isLegalService ||
                      user.position !== "Paralegal") && (
                        <Grid container spacing={2} sx={{ mt: 2, ml: 0.2 }}>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              label="Prefix"
                              value={user.prefix}
                              onChange={(e) =>
                                handleUserInputChange(
                                  index,
                                  "prefix",
                                  e.target.value
                                )
                              }
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              label="First Name"
                              value={user.firstName}
                              onChange={(e) =>
                                handleUserInputChange(
                                  index,
                                  "firstName",
                                  e.target.value
                                )
                              }
                              required
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              label="Middle Name"
                              value={user.middleName}
                              onChange={(e) =>
                                handleUserInputChange(
                                  index,
                                  "middleName",
                                  e.target.value
                                )
                              }
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              label="Last Name"
                              value={user.lastName}
                              onChange={(e) =>
                                handleUserInputChange(
                                  index,
                                  "lastName",
                                  e.target.value
                                )
                              }
                              required
                              fullWidth
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Suffix"
                              value={user.suffix}
                              onChange={(e) =>
                                handleUserInputChange(
                                  index,
                                  "suffix",
                                  e.target.value
                                )
                              }
                              select
                              fullWidth
                            >
                              <MenuItem value="">None</MenuItem>
                              <MenuItem value="Jr.">Jr.</MenuItem>
                              <MenuItem value="Sr.">Sr.</MenuItem>
                              <MenuItem value="II">I</MenuItem>
                              <MenuItem value="II">II</MenuItem>
                              <MenuItem value="III">III</MenuItem>
                              <MenuItem value="IV">IV</MenuItem>
                              <MenuItem value="V">V</MenuItem>
                            </TextField>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Email"
                              type="email"
                              value={user.email}
                              onChange={(e) =>
                                handleUserInputChange(
                                  index,
                                  "email",
                                  e.target.value
                                )
                              }
                              required
                              fullWidth
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={user.useFirmEmail}
                                  onChange={(e) =>
                                    handleUserInputChange(
                                      index,
                                      "useFirmEmail",
                                      e.target.checked
                                    )
                                  }
                                />
                              }
                              label="Use a different email for this firm's communications"
                            />
                          </Grid>

                          {user.useFirmEmail && (
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Firm-Specific Email"
                                type="email"
                                value={user.firmEmail}
                                onChange={(e) =>
                                  handleUserInputChange(
                                    index,
                                    "firmEmail",
                                    e.target.value
                                  )
                                }
                                fullWidth
                                helperText="This email will be used for communications related to this firm only. Login will still use the primary email above."
                              />
                            </Grid>
                          )}

                          <Grid item xs={12} sm={6}>
                            <InputMask
                              mask="(999) 999-9999"
                              value={user.phoneNumber}
                              onChange={(e) =>
                                handleUserInputChange(
                                  index,
                                  "phoneNumber",
                                  e.target.value
                                )
                              }
                            >
                              {(inputProps) => (
                                <TextField
                                  {...inputProps}
                                  label="Phone Number"
                                  fullWidth
                                />
                              )}
                            </InputMask>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Extension"
                              value={user.extension}
                              onChange={(e) =>
                                handleUserInputChange(
                                  index,
                                  "extension",
                                  e.target.value
                                )
                              }
                              fullWidth
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <InputLabel>Permission Level</InputLabel>
                              <Select
                                value={user.permissionLevel}
                                onChange={(e) =>
                                  handleUserInputChange(
                                    index,
                                    "permissionLevel",
                                    e.target.value
                                  )
                                }
                                label="Permission Level"
                              >
                                <MenuItem value="Admin">Admin</MenuItem>
                                <MenuItem value="User">User</MenuItem>
                                <MenuItem value="Viewer">Viewer</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                      )}

                    {/* GIA Section - Moved to the end of each user card */}
                    {user.position === "Attorney" && (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            mt: 3,
                            p: 2,
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="h6" sx={{ mb: 2 }}>
                            GIA Document
                          </Typography>

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={user.is_gia_on_file}
                                onChange={(e) =>
                                  handleUserInputChange(
                                    index,
                                    "is_gia_on_file",
                                    e.target.checked
                                  )
                                }
                              />
                            }
                            label="Is GIA on file?"
                          />

                          {user.is_gia_on_file && (
                            <>
                              {user.gia_document_link ? (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="body1">
                                    Existing GIA Document:
                                    <Link
                                      href={user.gia_document_link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{ ml: 1 }}
                                    >
                                      {user.gia_document_name || "Download GIA"}
                                    </Link>
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  sx={{ mb: 2 }}
                                >
                                  No GIA document uploaded yet.
                                </Typography>
                              )}

                              <Box>
                                <input
                                  accept=".pdf,.doc,.docx"
                                  style={{ display: "none" }}
                                  id={`gia-upload-${index}`}
                                  type="file"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      const updatedUsers = [...users];
                                      updatedUsers[index].gia_file = file;
                                      updatedUsers[index].gia_document_name =
                                        file.name;
                                      setUsers(updatedUsers);
                                    }
                                  }}
                                />
                                <label htmlFor={`gia-upload-${index}`}>
                                  <Button variant="contained" component="span">
                                    {user.gia_file
                                      ? "Change GIA Document"
                                      : "Upload New GIA Document"}
                                  </Button>
                                </label>
                                {user.gia_file && (
                                  <Typography
                                    variant="body2"
                                    sx={{ ml: 2, display: "inline" }}
                                  >
                                    {user.gia_document_name}
                                  </Typography>
                                )}
                              </Box>
                            </>
                          )}
                        </Box>
                      </Grid>
                    )}

                    {/* LOA Section - Moved to the end of each user card */}
                    {user.position === "Attorney" && (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            mt: 3,
                            p: 2,
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="h6" sx={{ mb: 2 }}>
                            LOA Document
                          </Typography>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={user.is_loa_on_file}
                                onChange={(e) =>
                                  handleUserInputChange(
                                    index,
                                    "is_loa_on_file",
                                    e.target.checked
                                  )
                                }
                              />
                            }
                            label="Is LOA on file?"
                          />

                          {user.is_loa_on_file && (
                            <>
                              {user.loa_document_link ? (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="body1">
                                    Existing LOA Document:
                                    <Link
                                      href={user.loa_document_link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{ ml: 1 }}
                                    >
                                      {user.loa_document_name || "Download LOA"}
                                    </Link>
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  sx={{ mb: 2 }}
                                >
                                  No LOA document uploaded yet.
                                </Typography>
                              )}

                              <Box>
                                <input
                                  accept=".pdf,.doc,.docx"
                                  style={{ display: "none" }}
                                  id={`loa-upload-${index}`}
                                  type="file"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      const updatedUsers = [...users];
                                      updatedUsers[index].loa_file = file;
                                      updatedUsers[index].loa_document_name =
                                        file.name;
                                      setUsers(updatedUsers);
                                    }
                                  }}
                                />
                                <label htmlFor={`loa-upload-${index}`}>
                                  <Button variant="contained" component="span">
                                    {user.loa_file
                                      ? "Change LOA Document"
                                      : "Upload New LOA Document"}
                                  </Button>
                                </label>
                                {user.loa_file && (
                                  <Typography
                                    variant="body2"
                                    sx={{ ml: 2, display: "inline" }}
                                  >
                                    {user.loa_document_name}
                                  </Typography>
                                )}
                              </Box>
                            </>
                          )}
                        </Box>
                      </Grid>
                    )}
                  {/* Send Invite Checkbox */}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={user.sendInvite}
                          onChange={(e) =>
                            handleUserInputChange(
                              index,
                              "sendInvite",
                              e.target.checked
                            )
                          }
                        />
                      }
                      label="Send Invite Email"
                    />
                  </Grid>
                  </Grid>
                </Box>
              ))}

              {/* Add Another User / Submit Buttons */}
              <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddUser}
                >
                  ADD ANOTHER USER
                </Button>
                <Box
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmitAllUsers}
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      "Submit All Users"
                    )}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      // If you'd like to reset users, etc.
                      setUsers([
                        {
                          prefix: "",
                          firstName: "",
                          lastName: "",
                          middleName: "",
                          suffix: "",
                          email: "",
                          phoneNumber: "",
                          extension: "",
                          position: "",
                          permissionLevel: "User",
                          isLegalService: false,
                          isDefault: false,
                          lawschool: "",
                          barNumber: "",
                          is_gia_on_file: false,
                          gia_document_link: "",
                          gia_document_name: "",
                          gia_file: null,
                          selectedProfessional: "",
                          showCustomForm: false,
                          location: "",
                        },
                      ]);
                    }}
                  >
                    Reset
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Paper>
      </Container>

    </>
  );
}

export default CreateLawFirm;
